"use client";

import { useState } from 'react';
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, Label } from 'keep-react';
import { ArrowLeft, ArrowRight, Check, Calendar } from 'phosphor-react';

interface SteppedBirthdayInputProps {
  name: string;
  control: Control<any>;
  label?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  defaultValue?: Date | string | null;
}

export const SteppedBirthdayInput = ({ 
  name, 
  control, 
  label, 
  required = false, 
  disabled = false, 
  error,
  defaultValue 
}: SteppedBirthdayInputProps) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<'year' | 'month' | 'day'>('year');
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const registerAge: number = parseInt(process.env.NEXT_PUBLIC_REGISTRATION_AGE || "16");
  // Generate year options (18-100 years old for birthday input)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 83 }, (_, i) => currentYear - registerAge - i);

  // Get days in month
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month, 0).getDate();
  };

  // Parse existing value on mount
  const parseValue = (value: Date | string | null) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "string") {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  };

  const updateSelectedValues = (date: Date | null) => {
    if (date) {
      setSelectedYear(date.getFullYear());
      setSelectedMonth(date.getMonth() + 1);
      setSelectedDay(date.getDate());
    } else {
      setSelectedYear(null);
      setSelectedMonth(null);
      setSelectedDay(null);
    }
  };

  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    setCurrentStep('month');
  };

  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
    setCurrentStep('day');
  };

  const handleDaySelect = (day: number, onChange: (value: Date) => void) => {
    if (selectedYear && selectedMonth) {
      setSelectedDay(day);
      const newDate = new Date(selectedYear, selectedMonth - 1, day);
      onChange(newDate);
      setIsOpen(false);
      setCurrentStep('year'); // Reset for next time
    }
  };

  const handlePrevious = () => {
    if (currentStep === 'month') {
      setCurrentStep('year');
    } else if (currentStep === 'day') {
      setCurrentStep('month');
    }
  };

  const handleReset = (onChange: (value: any) => void) => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setCurrentStep('year');
    onChange(null);
    setIsOpen(false);
  };

  const renderYearStep = (onChange: (value: Date) => void) => (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{t('date.labels.year')}</h3>
        <Button type="button" size="sm" variant="outline" onClick={() => handleReset(onChange)}>
          {t('common.cancel')}
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
        {yearOptions.map((year) => (
          <Button
            type="button"
            key={year}
            variant={selectedYear === year ? "default" : "outline"}
            size="sm"
            onClick={() => {
              handleYearSelect(year);
            }}
            className={`h-10 ${selectedYear === year ? 'bg-primary text-white hover:bg-primary' : ''}`}
          >
            {year}
          </Button>
        ))}
      </div>
    </div>
  );

  const renderMonthStep = (onChange: (value: Date) => void) => (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={handlePrevious}>
            <ArrowLeft size={16} />
          </Button>
          <h3 className="text-lg font-semibold">{t('date.labels.month')} {selectedYear}</h3>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => handleReset(onChange)}>
          {t('common.cancel')}
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: 12 }, (_, i) => (
          <Button
            type="button"
            key={i}
            variant={selectedMonth === i + 1 ? "default" : "outline"}
            size="sm"
            onClick={() => {
              handleMonthSelect(i + 1);
            }}
            className={`h-12 text-sm ${selectedMonth === i + 1 ? 'bg-primary text-white hover:bg-primary' : ''}`}
          >
            {t(`date.months.${i}`)}
          </Button>
        ))}
      </div>
    </div>
  );

  const renderDayStep = (onChange: (value: Date) => void) => {
    if (!selectedYear || !selectedMonth) return null;
    
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    return (
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" onClick={handlePrevious}>
              <ArrowLeft size={16} />
            </Button>
            <h3 className="text-lg font-semibold">
              {t('date.labels.day')} - {t(`date.months.${selectedMonth - 1}`)} {selectedYear}
            </h3>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={() => handleReset(onChange)}>
            {t('common.cancel')}
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1 max-h-48 overflow-y-auto">
          {days.map((day) => (
            <Button
              type="button"
              key={day}
              variant={selectedDay === day ? "default" : "outline"}
              size="sm"
              onClick={() => handleDaySelect(day, onChange)}
              className={`h-10 text-sm ${selectedDay === day ? 'bg-primary text-white hover:bg-primary' : ''}`}
            >
              {day}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  const getStepContent = (onChange: (value: Date) => void) => {
    switch (currentStep) {
      case 'year':
        return renderYearStep(onChange);
      case 'month':
        return renderMonthStep(onChange);
      case 'day':
        return renderDayStep(onChange);
      default:
        return null;
    }
  };

  const getProgressIndicator = () => {
    const steps = [
      { key: 'year', label: t('date.labels.year'), completed: selectedYear !== null },
      { key: 'month', label: t('date.labels.month'), completed: selectedMonth !== null },
      { key: 'day', label: t('date.labels.day'), completed: selectedDay !== null },
    ];

    return (
      <div className="flex items-center justify-center gap-2 p-2 border-b">
        {steps.map((step, index) => (
          <div key={step.key} className="flex items-center">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                step.completed
                  ? 'bg-primary text-white'
                  : currentStep === step.key
                  ? 'bg-primary/10 text-primary border-2 border-primary'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step.completed ? <Check size={16} /> : index + 1}
            </div>
            <span className={`ml-2 text-sm ${
              currentStep === step.key ? 'font-medium text-primary' : 'text-gray-600'
            }`}>
              {step.label}
            </span>
            {index < steps.length - 1 && (
              <ArrowRight size={16} className="mx-2 text-gray-400" />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const currentValue = parseValue(field.value || defaultValue);
        
        // Sync internal state with field value
        const hasValueChanged = JSON.stringify(currentValue) !== JSON.stringify(parseValue(field.value));
        
        if (hasValueChanged) {
          updateSelectedValues(currentValue);
        }

        return (
          <div className="space-y-2">
            {label && (
              <Label className="text-slate-600 font-medium text-sm">
                {label}
                {required && <span className="text-red-500">*</span>}
              </Label>
            )}
            
            <div className="relative">
              <div 
                onClick={() => !disabled && setIsOpen(true)}
                className={`w-full ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              >
                <div
                  className={`w-full justify-start gap-2 text-metal-900 opacity-60 border h-11 text-body-2 flex items-center leading-6 py-2 px-3 rounded-lg ${
                    error ? "border-red-500" : "border-gray-200"
                  }`}
                >
                  <Calendar
                    size={20}
                    className={error ? "text-red-500" : "text-metal-400"}
                  />
                  {currentValue ? (
                    <span>{formatDisplayDate(currentValue)}</span>
                  ) : (
                    <span className={error ? "text-red-500" : ""}>
                      {t("register.placeholders.dateOfBirth", "Geburtsdatum auswählen")}
                    </span>
                  )}
                </div>
              </div>

              {isOpen && !disabled && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                  {getProgressIndicator()}
                  {getStepContent(field.onChange)}
                </div>
              )}

              {isOpen && (
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => {
                    setIsOpen(false);
                    setCurrentStep('year');
                  }}
                />
              )}
            </div>

            {error && (
              <p className="text-sm text-red-500 flex items-center gap-1 mt-1">
                {error}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};